import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import AppScriptAPI from '../../services/appscriptAPI';
import './AnalyticsView.css'; // We'll create this separately

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

function AnalyticsView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get URL parameters
  const level = searchParams.get('level');
  const code = searchParams.get('code');
  const sheetId = searchParams.get('sheetId');

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [viewModes, setViewModes] = useState({}); // Track view mode for each category

  // Refs for scrolling
  const categoryRefs = useRef({});

  useEffect(() => {
    if (!level) {
      setError(
        'Missing required parameters. Please go back and make a selection.'
      );
      setLoading(false);
      return;
    }

    loadAnalytics();
  }, [level, code, sheetId]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await AppScriptAPI.getAnalytics(level, code, sheetId);

      if (!response || !response.success) {
        throw new Error(response.message || 'Failed to load analytics data.');
      }

      setAnalyticsData(response);

      // Initialize view modes (all start as 'table')
      const modes = {};
      response.tables.forEach((_, index) => {
        modes[index] = 'table';
      });
      setViewModes(modes);

      setLoading(false);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'An error occurred while loading analytics.');
      setLoading(false);
    }
  };

  const formatLevel = (lvl) => {
    const map = {
      'all-island': 'All Island',
      province: 'Province',
      district: 'District',
      'ds-division': 'DS Division',
    };
    return map[lvl] || lvl;
  };

  const formatNumber = (num) => {
    return parseInt(num).toLocaleString();
  };

  const changeViewMode = (index, mode) => {
    setViewModes((prev) => ({
      ...prev,
      [index]: mode,
    }));
  };

  const generateChartColors = (count) => {
    const baseColors = [
      { bg: 'rgba(102, 126, 234, 0.7)', border: 'rgba(102, 126, 234, 1)' },
      { bg: 'rgba(118, 75, 162, 0.7)', border: 'rgba(118, 75, 162, 1)' },
      { bg: 'rgba(240, 147, 251, 0.7)', border: 'rgba(240, 147, 251, 1)' },
      { bg: 'rgba(245, 87, 108, 0.7)', border: 'rgba(245, 87, 108, 1)' },
      { bg: 'rgba(67, 206, 162, 0.7)', border: 'rgba(67, 206, 162, 1)' },
      { bg: 'rgba(245, 166, 35, 0.7)', border: 'rgba(245, 166, 35, 1)' },
      { bg: 'rgba(248, 113, 113, 0.7)', border: 'rgba(248, 113, 113, 1)' },
      { bg: 'rgba(96, 165, 250, 0.7)', border: 'rgba(96, 165, 250, 1)' },
      { bg: 'rgba(168, 85, 247, 0.7)', border: 'rgba(168, 85, 247, 1)' },
      { bg: 'rgba(236, 72, 153, 0.7)', border: 'rgba(236, 72, 153, 1)' },
    ];

    const background = [];
    const border = [];

    for (let i = 0; i < count; i++) {
      const color = baseColors[i % baseColors.length];
      background.push(color.bg);
      border.push(color.border);
    }

    return { background, border };
  };

  const scrollToCategory = (index) => {
    if (categoryRefs.current[index]) {
      categoryRefs.current[index].scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const downloadSummary = () => {
    if (!analyticsData) return;

    let csv = 'Analytics Summary\n\n';
    csv += `Level,${formatLevel(analyticsData.level)}\n`;
    csv += `Location,${analyticsData.locationName}\n`;
    csv += `Total Persons,${analyticsData.totalPersons}\n`;
    csv += `Generated,${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}\n\n`;

    analyticsData.tables.forEach((table) => {
      csv += `\n${table.nameHeader},${table.countHeader}\n`;
      table.rows.forEach((row) => {
        csv += `"${row.name}",${row.count}\n`;
      });
    });

    downloadCSV(csv, `analytics-summary-${Date.now()}.csv`);
  };

  const downloadCategory = (index) => {
    if (!analyticsData || !analyticsData.tables[index]) return;

    const table = analyticsData.tables[index];
    let csv = `${table.nameHeader},${table.countHeader}\n`;

    table.rows.forEach((row) => {
      csv += `"${row.name}",${row.count}\n`;
    });

    const fileName = `${table.nameHeader
      .replace(/\s+/g, '-')
      .toLowerCase()}-${Date.now()}.csv`;
    downloadCSV(csv, fileName);
  };

  const downloadCSV = (content, fileName) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderCategoryTable = (table, index) => {
    const currentMode = viewModes[index] || 'table';

    const chartData = {
      labels: table.rows.map((row) => row.name),
      datasets: [
        {
          label: table.countHeader,
          data: table.rows.map((row) => parseInt(row.count)),
          backgroundColor: generateChartColors(table.rows.length).background,
          borderColor: generateChartColors(table.rows.length).border,
          borderWidth: 2,
        },
      ],
    };

    const barOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: table.nameHeader,
          font: {
            size: 16,
            weight: 'bold',
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            precision: 0,
          },
        },
      },
    };

    const pieOptions = {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 15,
            font: {
              size: 12,
            },
          },
        },
        title: {
          display: true,
          text: table.nameHeader,
          font: {
            size: 16,
            weight: 'bold',
          },
        },
      },
    };

    return (
      <div
        key={index}
        className="category-section"
        ref={(el) => (categoryRefs.current[index] = el)}
        id={`category-${index}`}
      >
        <div className="category-header">
          <span>{table.nameHeader}</span>
          <div className="view-controls">
            <button
              className={`view-btn ${currentMode === 'table' ? 'active' : ''}`}
              onClick={() => changeViewMode(index, 'table')}
            >
              <i className="bi bi-table me-1"></i>Table
            </button>
            <button
              className={`view-btn ${currentMode === 'bar' ? 'active' : ''}`}
              onClick={() => changeViewMode(index, 'bar')}
            >
              <i className="bi bi-bar-chart-fill me-1"></i>Bar Chart
            </button>
            <button
              className={`view-btn ${currentMode === 'pie' ? 'active' : ''}`}
              onClick={() => changeViewMode(index, 'pie')}
            >
              <i className="bi bi-pie-chart-fill me-1"></i>Pie Chart
            </button>
            <button
              className="download-btn"
              onClick={() => downloadCategory(index)}
            >
              <i className="bi bi-download me-1"></i>Download
            </button>
          </div>
        </div>

        {/* Table View */}
        {currentMode === 'table' && (
          <div className="table-container">
            <Table hover className="data-table">
              <thead>
                <tr>
                  <th>{table.nameHeader}</th>
                  <th className="text-end" style={{ width: '150px' }}>
                    {table.countHeader}
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.rows && table.rows.length > 0 ? (
                  table.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      <td>{row.name}</td>
                      <td className="text-end">
                        <span className="count-badge">
                          {formatNumber(row.count)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="text-center text-muted">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}

        {/* Bar Chart View */}
        {currentMode === 'bar' && table.rows && table.rows.length > 0 && (
          <div className="chart-container">
            <Bar data={chartData} options={barOptions} />
          </div>
        )}

        {/* Pie Chart View */}
        {currentMode === 'pie' && table.rows && table.rows.length > 0 && (
          <div className="chart-container">
            <Pie data={chartData} options={pieOptions} />
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-content">
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: '4rem', height: '4rem' }}
          />
          <p className="mt-3 fw-bold">Loading Analytics Data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading><i className="bi bi-exclamation-triangle-fill me-2"></i>Error Loading Data</Alert.Heading>
          <p>{error}</p>
          <hr />
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => navigate('/analytics/select')}
          >
            Go Back to Selection
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="analytics-page">
      <Row className="justify-content-center">
        <Col xs={12} xl={10}>
          {/* Back Button */}
          <div className="mb-3">
            <Button
              className="back-button"
              onClick={() => navigate('/analytics/select')}
            >
              <><i className="bi bi-arrow-left me-1"></i>Back to Selection</>
            </Button>
          </div>

          {/* Analytics Header */}
          <div className="analytics-header">
            <h2 className="mb-0">Analytics Dashboard</h2>
          </div>

          {/* Breadcrumb */}
          <div className="breadcrumb-section">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">Analytics</li>
                <li className="breadcrumb-item active">
                  {formatLevel(analyticsData?.level)}
                </li>
                {analyticsData?.locationName && (
                  <li className="breadcrumb-item active">
                    {analyticsData.locationName}
                  </li>
                )}
              </ol>
            </nav>
          </div>

          {/* Total Persons Card */}
          <div className="total-card">
            <h3>Total Persons Registered</h3>
            <div className="total-number">
              {formatNumber(analyticsData?.totalPersons || 0)}
            </div>
            <p className="mb-0 opacity-75">Records in this area</p>
            <Button variant="light" className="mt-3" onClick={downloadSummary}>
              <i className="bi bi-download me-1"></i>Download Summary
            </Button>
          </div>

          {/* Quick Navigation Section */}
          <div className="quick-navigation-section">
            <h5 className="quick-nav-title"><i className="bi bi-list-ul me-2"></i>Quick Navigation</h5>
            <div className="quick-nav-grid">
              {analyticsData?.tables?.map((table, index) => (
                <button
                  key={index}
                  className="quick-nav-item"
                  onClick={() => scrollToCategory(index)}
                >
                  <span className="quick-nav-icon"></span>
                  <span className="quick-nav-text">{table.nameHeader}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="content-wrapper">
            {analyticsData?.tables?.map((table, index) =>
              renderCategoryTable(table, index)
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default AnalyticsView;